// Hooks
import { useEffect } from 'react';

import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';

import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

// Types
import type { SelectedProfilesProps } from '@/components/composite/SelectedProfiles';
import { GroupItem } from '@/components/composite/SelectedProfiles/MultiSelectionProfiles/GroupItem';
// Components
import { MultiProfileItem } from '@/components/composite/SelectedProfiles/MultiSelectionProfiles/MultiProfileItem';
import { ProfilesList } from '@/components/composite/SelectedProfiles/ProfilesList';

export function MultiSelectionProfiles({ itemsSize }: Required<SelectedProfilesProps>) {
    const { activatedSocialProfiles } = useSocialProfiles({ revalidateIfStale: false });
    const { activeSocialProfileId, activeMultiSelectionProfiles } = useProfilesStore(
        (state) => state
    );
    const { activeGroup, setActiveGroup } = useActiveGroup();
    const { activatedSpGroups } = useSPGroups({ revalidateIfStale: false });

    const profiles =
        activeGroup?.social_profiles.filter((profiles) => profiles.active) ??
        activeMultiSelectionProfiles?.socialProfileIds.map((profileId) => {
            return activatedSocialProfiles.find((profile) => profile.id === profileId);
        });

    const group =
        activeGroup ??
        activatedSpGroups.find((group) => {
            if (group.social_profiles.length !== profiles.length) {
                return false;
            }
            return profiles.every((profile) =>
                group.social_profiles.some((groupProfile) => groupProfile.id === profile.id)
            );
        });

    useEffect(() => {
        if (!activeSocialProfileId && group) {
            setActiveGroup(group);
        }
    }, [activeSocialProfileId]);

    return (
        <div className="flex items-center gap-4">
            {group ? (
                <GroupItem itemsSize={itemsSize} group={group} isActive={!activeSocialProfileId} />
            ) : (
                <MultiProfileItem itemsSize={itemsSize} isActive={!activeSocialProfileId} />
            )}
            <span className="h-5 min-w-[2px] bg-icoGray-300 dark:bg-icoDarkMode-beton" />
            <ProfilesList itemsSize={itemsSize} profiles={profiles} />
        </div>
    );
}
